'use client';

import { useState, useEffect } from 'react';
import { useSession } from 'next-auth/react';
import { Languages, Sparkles, Trophy, BookOpen, Globe, GraduationCap, ArrowLeftRight } from 'lucide-react';
import Link from 'next/link';
import AgeVerificationModal from '@/components/learning/AgeVerificationModal';
import AgeCategorySection from '@/components/learning/AgeCategorySection';
import ParentGuidance from '@/components/learning/ParentGuidance';
import ProgressTracker from '@/components/learning/ProgressTracker';
import { getAgeMetadata } from '@/lib/learning-content-prompts';
import type { UserProgress, LevelInfo } from '@/lib/learning-progress';

interface AudioBook {
  id: string;
  title: string;
  prompt: string;
  coverImageUrl?: string;
  language: string;
  targetLanguage?: string;
  difficultyLevel: number;
  learningTopic?: string;
  ageCategory: string;
}

const AGE_CATEGORIES = ['0-1', '1-2', '3-4', '5-6', '7-8', '9-10'];

// Helper function to convert AGE_1_2 format to 1-2 format
const convertAgeFormat = (ageRange: string): string => {
  if (!ageRange) return '';
  // Convert AGE_1_2 to 1-2
  return ageRange.replace('AGE_', '').replace('_', '-');
};

const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Spanish' },
  { code: 'fr', name: 'French' },
  { code: 'de', name: 'German' },
  { code: 'it', name: 'Italian' },
  { code: 'pt', name: 'Portuguese' },
  { code: 'nl', name: 'Dutch' },
  { code: 'ja', name: 'Japanese' },
  { code: 'ko', name: 'Korean' },
  { code: 'zh', name: 'Chinese' },
  { code: 'ar', name: 'Arabic' },
  { code: 'pl', name: 'Polish' },
  { code: 'lt', name: 'Lithuanian' },
];

export default function LearningModePage() {
  const { data: session, status } = useSession();
  const [loading, setLoading] = useState(true);
  const [storiesByAge, setStoriesByAge] = useState<Record<string, AudioBook[]>>({});
  const [completionStatus, setCompletionStatus] = useState<Record<string, boolean>>({});
  const [allProgress, setAllProgress] = useState<UserProgress[]>([]);
  const [showAgeModal, setShowAgeModal] = useState(false);
  const [selectedLanguagePair, setSelectedLanguagePair] = useState<string>('en-es');
  const [overallStats, setOverallStats] = useState<any>(null);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>(
    AGE_CATEGORIES.reduce((acc, age) => ({ ...acc, [age]: true }), {})
  );
  const [userKidAge, setUserKidAge] = useState<string>('');
  const [showAllAges, setShowAllAges] = useState(false);

  useEffect(() => {
    checkAgeAndFetchContent();
  }, [session]);

  useEffect(() => {
    if (selectedLanguagePair) {
      fetchLearningContent();
    }
  }, [selectedLanguagePair]);

  const checkAgeAndFetchContent = async () => {
    if (status === 'loading') return;

    // Check if user has age set
    if (session?.user) {
      const kidAgeRange = (session.user as any).kidAgeRange;
      if (!kidAgeRange) {
        setShowAgeModal(true);
      } else {
        // Convert and store the user's kid age (e.g., AGE_1_2 -> 1-2)
        const convertedAge = convertAgeFormat(kidAgeRange);
        setUserKidAge(convertedAge);
      }
      
      // Fetch progress
      await fetchProgress();
      await fetchStatistics();
    }

    // Fetch content regardless of login status
    await fetchLearningContent();
  };

  const fetchLearningContent = async () => {
    try {
      setLoading(true);
      const url = `/api/learning/content?languagePair=${selectedLanguagePair}`;
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setStoriesByAge(data.storiesByAge || {});
        setCompletionStatus(data.completionStatus || {});
      }
    } catch (error) {
      console.error('Failed to fetch learning content:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProgress = async () => {
    if (!session?.user) return;

    try {
      const response = await fetch('/api/learning/progress');
      if (response.ok) {
        const data = await response.json();
        setAllProgress(data.progress || []);
      }
    } catch (error) {
      console.error('Failed to fetch progress:', error);
    }
  };

  const fetchStatistics = async () => {
    if (!session?.user) return;

    try {
      const response = await fetch('/api/learning/level?action=statistics');
      if (response.ok) {
        const data = await response.json();
        setOverallStats(data.statistics);
      }
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
    }
  };

  const handleAgeSelected = (ageRange: string) => {
    setShowAgeModal(false);
    // Refresh the page to update session
    window.location.reload();
  };

  const toggleSection = (ageCategory: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [ageCategory]: !prev[ageCategory],
    }));
  };

  const getProgressForAge = (ageCategory: string): UserProgress | undefined => {
    return allProgress.find((p) => p.ageCategory === ageCategory);
  };

  const getLevelInfoForAge = (ageCategory: string): LevelInfo | undefined => {
    const progress = getProgressForAge(ageCategory);
    if (!progress) return undefined;

    return {
      level: progress.currentLevel,
      title: `Level ${progress.currentLevel}`,
      description: 'Learning in progress',
      storiesRequired: 5,
      storiesCompleted: progress.storiesCompleted % 5,
      nextLevel: progress.currentLevel + 1,
    };
  };

  if (loading && status === 'loading') {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #C9B6E4 0%, #A8D8E6 25%, #F8C8A0 50%, #B4A0E5 75%, #C9B6E4 100%)',
          backgroundSize: '400% 400%',
          animation: 'gradientFlow 15s ease infinite',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          paddingTop: '80px',
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <div
            style={{
              width: '80px',
              height: '80px',
              border: '5px solid rgba(255, 255, 255, 0.2)',
              borderTopColor: 'white',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 24px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
            }}
          />
          <p style={{ 
            color: 'white', 
            fontSize: 'clamp(1.125rem, 3vw, 1.5rem)', 
            fontWeight: 700,
            textShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
          }}>
            Loading Your Learning Adventure...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #C9B6E4 0%, #A8D8E6 25%, #F8C8A0 50%, #B4A0E5 75%, #C9B6E4 100%)',
        backgroundSize: '400% 400%',
        animation: 'gradientFlow 15s ease infinite',
         paddingTop: 'clamp(4.5rem, 6vw, 5rem)',
        paddingBottom: 'clamp(3rem, 8vw, 5rem)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Animated Background Orbs */}
      <div style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        overflow: 'hidden',
        zIndex: 0,
      }}>
        {/* Orb 1 */}
        <div style={{
          position: 'absolute',
          top: '5%',
          left: '10%',
          width: 'clamp(300px, 40vw, 600px)',
          height: 'clamp(300px, 40vw, 600px)',
          background: 'radial-gradient(circle, rgba(201, 182, 228, 0.4) 0%, transparent 70%)',
          borderRadius: '50%',
          animation: 'float1 25s ease-in-out infinite',
          filter: 'blur(60px)',
        }} />
        
        {/* Orb 2 */}
        <div style={{
          position: 'absolute',
          top: '30%',
          right: '5%',
          width: 'clamp(250px, 35vw, 500px)',
          height: 'clamp(250px, 35vw, 500px)',
          background: 'radial-gradient(circle, rgba(168, 216, 230, 0.35) 0%, transparent 70%)',
          borderRadius: '50%',
          animation: 'float2 20s ease-in-out infinite',
          filter: 'blur(60px)',
        }} />
        
        {/* Orb 3 */}
        <div style={{
          position: 'absolute',
          bottom: '15%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: 'clamp(280px, 38vw, 550px)',
          height: 'clamp(280px, 38vw, 550px)',
          background: 'radial-gradient(circle, rgba(248, 200, 160, 0.3) 0%, transparent 70%)',
          borderRadius: '50%',
          animation: 'float3 28s ease-in-out infinite',
          filter: 'blur(60px)',
        }} />
      </div>

      {/* Floating Sparkles for Educational Magic */}
      {[...Array(12)].map((_, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            width: 'clamp(4px, 1vw, 8px)',
            height: 'clamp(4px, 1vw, 8px)',
            background: 'linear-gradient(135deg, #FFFFFF, #F8C8A0)',
            borderRadius: '50%',
            top: `${Math.random() * 100}%`,
            left: `${Math.random() * 100}%`,
            animation: `sparkleFloat ${4 + Math.random() * 5}s ease-in-out infinite`,
            animationDelay: `${Math.random() * 3}s`,
            opacity: 0.7,
            boxShadow: '0 0 15px rgba(255, 255, 255, 0.8)',
            pointerEvents: 'none',
            zIndex: 0,
          }}
        />
      ))}
      {/* Age Verification Modal */}
      <AgeVerificationModal
        isOpen={showAgeModal}
        onClose={() => setShowAgeModal(false)}
        onAgeSelected={handleAgeSelected}
      />

      {/* Hero Section - Award-Winning 2026 Design */}
      <div
        style={{
          maxWidth: '1400px',
          margin: '0 auto',
           padding: 'clamp(0.5rem, 1vw, 0.75rem) clamp(1.25rem, 3vw, 2rem) clamp(2rem, 4vw, 3rem)',
          textAlign: 'center',
          position: 'relative',
          zIndex: 1,
        }}
      >
        {/* Badge - Enhanced with Animation */}
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'clamp(0.5rem, 2vw, 0.75rem)',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            padding: 'clamp(0.625rem, 2vw, 0.75rem) clamp(1.25rem, 3vw, 1.75rem)',
            borderRadius: '50px',
            border: '2px solid rgba(255, 255, 255, 0.8)',
            marginBottom: 'clamp(0.625rem, 1.5vw, 0.875rem)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(255, 255, 255, 0.5) inset',
            animation: 'fadeIn 0.8s ease-out, pulse 3s ease-in-out infinite',
          }}
        >
          <div style={{
            background: 'linear-gradient(135deg, #C9B6E4, #A8D8E6)',
            borderRadius: '50%',
            width: 'clamp(24px, 4vw, 32px)',
            height: 'clamp(24px, 4vw, 32px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            animation: 'iconFloat 3s ease-in-out infinite',
          }}>
            <Sparkles size={16} color="white" strokeWidth={2.5} />
          </div>
          <div style={{
            background: 'linear-gradient(135deg, #10b981, #059669)',
            borderRadius: '50%',
            width: 'clamp(20px, 3vw, 24px)',
            height: 'clamp(20px, 3vw, 24px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <GraduationCap size={14} color="white" strokeWidth={2.5} />
          </div>
          <span
            style={{
              background: 'linear-gradient(135deg, #B4A0E5, #7B68EE, #A8D8E6)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              fontWeight: 800,
              fontSize: 'clamp(0.75rem, 2vw, 0.9375rem)',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
            }}
          >
            Montessori Bilingual Learning
          </span>
        </div>

        {/* Hero Title - Magical Gradient */}
        <h1
          style={{
            fontSize: 'clamp(2.5rem, 8vw, 4.5rem)',
            fontWeight: 900,
            background: 'linear-gradient(135deg, #FFFFFF 0%, #F0F9FF 30%, #FFFFFF 60%, #FFF5EB 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            marginBottom: 'clamp(0.75rem, 2vw, 1rem)',
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
            textShadow: '0 4px 30px rgba(0, 0, 0, 0.15)',
            animation: 'fadeIn 0.8s ease-out 0.2s backwards',
          }}
        >
          Learning Adventure
        </h1>

        <p
          style={{
            fontSize: 'clamp(1.125rem, 3vw, 1.5rem)',
            color: 'rgba(255, 255, 255, 0.98)',
            maxWidth: '900px',
            margin: '0 auto clamp(1.5rem, 3vw, 2rem)',
            lineHeight: 1.7,
            fontWeight: 600,
            textShadow: '0 2px 15px rgba(0, 0, 0, 0.1)',
            animation: 'fadeIn 0.8s ease-out 0.4s backwards',
          }}
        >
          Age-structured <span style={{ color: '#FFF', fontWeight: 800 }}>bilingual learning</span> following{' '}
          <span style={{ color: '#FFF', fontWeight: 800 }}>Montessori pedagogy</span> for children{' '}
          <span style={{ color: '#FFF', fontWeight: 800, background: 'rgba(255, 255, 255, 0.15)', padding: '0.125rem 0.5rem', borderRadius: '8px' }}>0-10 years</span>
        </p>

        {/* Language Selector - Modern Design */}
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'clamp(0.75rem, 2vw, 1rem)',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            padding: 'clamp(1rem, 2vw, 1.25rem) clamp(1.5rem, 3vw, 2rem)',
            borderRadius: '20px',
            border: '2px solid rgba(255, 255, 255, 0.8)',
            marginBottom: 'clamp(2rem, 4vw, 2.5rem)',
            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(255, 255, 255, 0.5) inset',
            animation: 'fadeIn 0.8s ease-out 0.6s backwards',
            flexWrap: 'wrap',
            justifyContent: 'center',
          }}
        >
          <div style={{
            background: 'linear-gradient(135deg, #A8D8E6, #7EC8E3)',
            borderRadius: '50%',
            width: 'clamp(32px, 5vw, 40px)',
            height: 'clamp(32px, 5vw, 40px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <Globe size={20} color="white" strokeWidth={2.5} />
          </div>
          <select
            value={selectedLanguagePair.split('-')[0]}
            onChange={(e) => {
              const newPair = `${e.target.value}-${selectedLanguagePair.split('-')[1]}`;
              setSelectedLanguagePair(newPair);
            }}
            style={{
              background: 'linear-gradient(135deg, #F0F9FF, #E0F2FE)',
              border: '2px solid rgba(168, 216, 230, 0.4)',
              borderRadius: '12px',
              padding: 'clamp(0.625rem, 2vw, 0.75rem) clamp(0.875rem, 2vw, 1rem)',
              color: '#1E40AF',
              fontSize: 'clamp(0.875rem, 2vw, 1rem)',
              fontWeight: 700,
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(168, 216, 230, 0.2)',
              transition: 'all 0.3s ease',
            }}
          >
            {LANGUAGES.map((lang) => (
              <option key={lang.code} value={lang.code} style={{ color: '#1a202c' }}>
                {lang.name}
              </option>
            ))}
          </select>
          <div style={{
            background: 'linear-gradient(135deg, #B4A0E5, #A8D8E6)',
            borderRadius: '50%',
            width: 'clamp(32px, 5vw, 40px)',
            height: 'clamp(32px, 5vw, 40px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <ArrowLeftRight size={20} color="white" strokeWidth={2.5} />
          </div>
          <select
            value={selectedLanguagePair.split('-')[1]}
            onChange={(e) => {
              const newPair = `${selectedLanguagePair.split('-')[0]}-${e.target.value}`;
              setSelectedLanguagePair(newPair);
            }}
            style={{
              background: 'linear-gradient(135deg, #FFF5EB, #FEF3E2)',
              border: '2px solid rgba(248, 200, 160, 0.4)',
              borderRadius: '12px',
              padding: 'clamp(0.625rem, 2vw, 0.75rem) clamp(0.875rem, 2vw, 1rem)',
              color: '#92400E',
              fontSize: 'clamp(0.875rem, 2vw, 1rem)',
              fontWeight: 700,
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(248, 200, 160, 0.2)',
              transition: 'all 0.3s ease',
            }}
          >
            {LANGUAGES.filter(
              (lang) => lang.code !== selectedLanguagePair.split('-')[0]
            ).map((lang) => (
              <option key={lang.code} value={lang.code} style={{ color: '#1a202c' }}>
                {lang.name}
              </option>
            ))}
          </select>
        </div>

        {/* Age Filter Info Badge */}
        {userKidAge && !showAllAges && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            marginTop: 'clamp(1.5rem, 3vw, 2rem)',
            animation: 'fadeIn 0.8s ease-out 0.6s backwards',
          }}>
            <div style={{
              background: 'linear-gradient(135deg, #10B981, #059669)',
              color: 'white',
              padding: 'clamp(0.625rem, 1.5vw, 0.875rem) clamp(1.25rem, 3vw, 1.75rem)',
              borderRadius: '14px',
              fontSize: 'clamp(0.875rem, 2vw, 1rem)',
              fontWeight: 700,
              boxShadow: '0 8px 24px rgba(16, 185, 129, 0.3)',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}>
              <div style={{
                background: 'rgba(255, 255, 255, 0.25)',
                borderRadius: '50%',
                width: '24px',
                height: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <GraduationCap size={14} strokeWidth={2.5} />
              </div>
              Showing content for {userKidAge} years old
            </div>
          </div>
        )}

        {/* Overall Stats - Award-Winning Cards */}
        {session?.user && overallStats && (
          <div
            style={{
              display: 'flex',
              gap: 'clamp(1rem, 3vw, 1.5rem)',
              justifyContent: 'center',
              flexWrap: 'wrap',
              maxWidth: '800px',
              margin: '0 auto',
              animation: 'fadeIn 0.8s ease-out 0.8s backwards',
            }}
          >
            {/* Stories Completed Card */}
            <div
              style={{
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                padding: 'clamp(1.25rem, 3vw, 1.75rem) clamp(1.5rem, 3vw, 2rem)',
                borderRadius: '20px',
                border: '2px solid rgba(255, 255, 255, 0.8)',
                minWidth: 'clamp(160px, 25vw, 220px)',
                flex: 1,
                boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(255, 255, 255, 0.5) inset',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
              className="stat-card"
            >
              <div style={{
                background: 'linear-gradient(135deg, #FFD700, #FFA500)',
                borderRadius: '16px',
                width: 'clamp(48px, 8vw, 60px)',
                height: 'clamp(48px, 8vw, 60px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 'clamp(0.875rem, 2vw, 1.25rem)',
                boxShadow: '0 8px 24px rgba(255, 215, 0, 0.35)',
                animation: 'iconFloat 3s ease-in-out infinite',
              }}>
                <Trophy size={28} color="white" strokeWidth={2.5} />
              </div>
              <div style={{ 
                fontSize: 'clamp(2rem, 5vw, 3rem)', 
                fontWeight: 900, 
                background: 'linear-gradient(135deg, #FFD700, #FFA500)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                marginBottom: '0.5rem',
                lineHeight: 1,
              }}>
                {overallStats.totalCompleted}
              </div>
              <div style={{ 
                fontSize: 'clamp(0.875rem, 2vw, 1rem)', 
                color: '#64748B', 
                fontWeight: 700,
                letterSpacing: '0.02em',
              }}>
                Stories Completed
              </div>
            </div>

            {/* Average Level Card */}
            <div
              style={{
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                padding: 'clamp(1.25rem, 3vw, 1.75rem) clamp(1.5rem, 3vw, 2rem)',
                borderRadius: '20px',
                border: '2px solid rgba(255, 255, 255, 0.8)',
                minWidth: 'clamp(160px, 25vw, 220px)',
                flex: 1,
                boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(255, 255, 255, 0.5) inset',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
              className="stat-card"
            >
              <div style={{
                background: 'linear-gradient(135deg, #B4A0E5, #A8D8E6)',
                borderRadius: '16px',
                width: 'clamp(48px, 8vw, 60px)',
                height: 'clamp(48px, 8vw, 60px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 'clamp(0.875rem, 2vw, 1.25rem)',
                boxShadow: '0 8px 24px rgba(180, 160, 229, 0.35)',
                animation: 'iconFloat 3s ease-in-out infinite 0.3s',
              }}>
                <BookOpen size={28} color="white" strokeWidth={2.5} />
              </div>
              <div style={{ 
                fontSize: 'clamp(2rem, 5vw, 3rem)', 
                fontWeight: 900, 
                background: 'linear-gradient(135deg, #B4A0E5, #A8D8E6)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                marginBottom: '0.5rem',
                lineHeight: 1,
              }}>
                {Math.round(overallStats.averageLevel * 10) / 10}
              </div>
              <div style={{ 
                fontSize: 'clamp(0.875rem, 2vw, 1rem)', 
                color: '#64748B', 
                fontWeight: 700,
                letterSpacing: '0.02em',
              }}>
                Average Level
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div style={{ 
        maxWidth: '1400px', 
        margin: '0 auto', 
        padding: '0 clamp(1.25rem, 3vw, 2rem)',
        position: 'relative',
        zIndex: 1,
      }}>
        {/* Parent Guidance */}
        <ParentGuidance />

        {/* Toggle to View All Ages (only show if user has selected age) */}
        {userKidAge && (
          <div style={{
            textAlign: 'center',
            marginBottom: 'clamp(2rem, 4vw, 3rem)',
          }}>
            <button
              onClick={() => setShowAllAges(!showAllAges)}
              style={{
                background: showAllAges 
                  ? 'linear-gradient(135deg, #B4A0E5, #A8D8E6)' 
                  : 'linear-gradient(135deg, #10B981, #059669)',
                color: 'white',
                border: 'none',
                borderRadius: '16px',
                padding: 'clamp(0.75rem, 2vw, 1rem) clamp(1.5rem, 3vw, 2rem)',
                fontSize: 'clamp(0.9375rem, 2vw, 1.0625rem)',
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
                transition: 'all 0.3s ease',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 12px 32px rgba(0, 0, 0, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.15)';
              }}
            >
              {showAllAges ? (
                <>
                  <BookOpen size={20} strokeWidth={2.5} />
                  Show Only My Kid's Age ({userKidAge} years)
                </>
              ) : (
                <>
                  <Globe size={20} strokeWidth={2.5} />
                  View All Age Categories
                </>
              )}
            </button>
          </div>
        )}

        {/* Age Category Sections */}
        {(() => {
          // Filter age categories based on user's selection
          const categoriesToShow = userKidAge && !showAllAges
            ? AGE_CATEGORIES.filter(age => age === userKidAge)
            : AGE_CATEGORIES;

          return categoriesToShow.map((ageCategory) => {
            const metadata = getAgeMetadata(ageCategory as any);
            const stories = storiesByAge[ageCategory] || [];
            const progress = getProgressForAge(ageCategory);
            const levelInfo = getLevelInfoForAge(ageCategory);

            return (
              <AgeCategorySection
                key={ageCategory}
                ageCategory={ageCategory}
                title={metadata.title}
                description={metadata.description}
                icon={metadata.icon}
                stories={stories}
                userProgress={progress}
                levelInfo={levelInfo}
                completionStatus={completionStatus}
                montessoriInfo={{
                  sensitivePeriods: metadata.sensitivePeriods,
                  developmentalFocus: metadata.developmentalFocus,
                }}
                isExpanded={expandedSections[ageCategory]}
                onToggle={() => toggleSection(ageCategory)}
              />
            );
          });
        })()}

        {/* CTA to Create Stories - Award-Winning Design */}
        <div
          style={{
            textAlign: 'center',
            padding: 'clamp(3rem, 6vw, 4rem) clamp(1.25rem, 3vw, 2rem)',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(30px)',
            WebkitBackdropFilter: 'blur(30px)',
            borderRadius: '32px',
            border: '2px solid rgba(255, 255, 255, 0.8)',
            marginTop: 'clamp(3rem, 6vw, 4rem)',
            boxShadow: '0 30px 80px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(255, 255, 255, 0.5) inset',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Decorative Background Gradient */}
          <div style={{
            position: 'absolute',
            top: '-50%',
            left: '-50%',
            width: '200%',
            height: '200%',
            background: 'radial-gradient(circle, rgba(201, 182, 228, 0.1) 0%, transparent 70%)',
            animation: 'float1 20s ease-in-out infinite',
            pointerEvents: 'none',
          }} />

          <div style={{ position: 'relative', zIndex: 1 }}>
            {/* Icon */}
            <div style={{
              width: 'clamp(80px, 12vw, 100px)',
              height: 'clamp(80px, 12vw, 100px)',
              margin: '0 auto clamp(1.5rem, 3vw, 2rem)',
              background: 'linear-gradient(135deg, #C9B6E4, #A8D8E6)',
              borderRadius: '28px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 20px 60px rgba(201, 182, 228, 0.4)',
              animation: 'iconFloat 3s ease-in-out infinite',
              transform: 'rotate(-5deg)',
            }}>
              <Sparkles size={48} color="white" strokeWidth={2.5} />
            </div>

            <h3
              style={{
                background: 'linear-gradient(135deg, #B4A0E5, #7B68EE, #A8D8E6)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
                fontWeight: 900,
                marginBottom: 'clamp(0.75rem, 1.5vw, 1rem)',
                letterSpacing: '-0.01em',
              }}
            >
              Create Your Own Learning Stories
            </h3>
            <p
              style={{
                color: '#64748B',
                fontSize: 'clamp(1rem, 2.5vw, 1.25rem)',
                marginBottom: 'clamp(2rem, 4vw, 2.5rem)',
                maxWidth: '700px',
                margin: '0 auto clamp(2rem, 4vw, 2.5rem)',
                lineHeight: 1.7,
                fontWeight: 600,
              }}
            >
              Generate <span style={{ color: '#B4A0E5', fontWeight: 800 }}>custom bilingual stories</span> tailored to your child's{' '}
              <span style={{ color: '#A8D8E6', fontWeight: 800 }}>interests</span> and{' '}
              <span style={{ color: '#F8C8A0', fontWeight: 800 }}>learning goals</span>
            </p>
            <Link
              href="/audio-book/create"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 'clamp(0.75rem, 2vw, 1rem)',
                padding: 'clamp(1rem, 2.5vw, 1.25rem) clamp(2rem, 4vw, 3rem)',
                background: 'linear-gradient(135deg, #C9B6E4 0%, #A8D8E6 50%, #B4A0E5 100%)',
                backgroundSize: '200% auto',
                color: 'white',
                borderRadius: '18px',
                fontWeight: 900,
                fontSize: 'clamp(1rem, 2.5vw, 1.25rem)',
                textDecoration: 'none',
                boxShadow: '0 12px 40px rgba(180, 160, 229, 0.4)',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                border: '2px solid rgba(255, 255, 255, 0.8)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px) scale(1.03)';
                e.currentTarget.style.boxShadow = '0 20px 60px rgba(180, 160, 229, 0.5)';
                e.currentTarget.style.backgroundPosition = 'right center';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0) scale(1)';
                e.currentTarget.style.boxShadow = '0 12px 40px rgba(180, 160, 229, 0.4)';
                e.currentTarget.style.backgroundPosition = 'left center';
              }}
            >
              <Sparkles size={24} strokeWidth={2.5} />
              Create Learning Story
            </Link>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes gradientFlow {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        
        @keyframes float1 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -30px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
        }
        
        @keyframes float2 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(-30px, 30px) scale(1.05); }
          66% { transform: translate(25px, -20px) scale(0.95); }
        }
        
        @keyframes float3 {
          0%, 100% { transform: translate(-50%, 0) scale(1); }
          50% { transform: translate(-50%, -25px) scale(1.08); }
        }
        
        @keyframes sparkleFloat {
          0%, 100% {
            transform: translateY(0) scale(1);
            opacity: 0.7;
          }
          50% {
            transform: translateY(-25px) scale(1.3);
            opacity: 1;
          }
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.02);
          }
        }
        
        @keyframes iconFloat {
          0%, 100% {
            transform: translateY(0) rotate(-5deg);
          }
          50% {
            transform: translateY(-10px) rotate(-5deg);
          }
        }
        
        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }
        
        .stat-card:hover {
          transform: translateY(-6px) scale(1.03);
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.18), 0 0 0 1px rgba(255, 255, 255, 0.5) inset !important;
        }
        
        @media (max-width: 768px) {
          .stat-card {
            min-width: 100% !important;
          }
        }
      `}</style>
    </div>
  );
}
